import classNames from 'classnames';
import {
  ControllerFieldState,
  ControllerRenderProps,
  FieldPath,
  FieldValues,
  useController,
  UseControllerProps,
  useFormContext,
  UseFormStateReturn,
} from 'react-hook-form';
import { TodoYup } from 'yup';

import { WithFieldStateProps } from '../../hooks';
import { HelperText, HelperTextProps } from '../helper-text';
import { Label, LabelProps } from '../label';
import { Skeleton, SkeletonProps } from '../skeleton';

// TODO overwritting value & onChange in order to ignore enums and null value to be passed to input which causes errors
export type FormFieldRenderProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<ControllerRenderProps<TFieldValues, TName>, 'value' | 'onChange'> &
  WithFieldStateProps & {
    required?: boolean;
    // value: string | ReadonlyArray<string> | number | undefined,
    value: TodoYup;
    onChange: TodoYup;
  };

export interface FormFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends UseControllerProps<TFieldValues, TName>,
    WithFieldStateProps {
  // additional components
  label?: React.ReactNode;
  LabelProps?: LabelProps;
  helperText?: React.ReactNode;
  HelperTextProps?: HelperTextProps;
  loading?: boolean;
  SkeletonProps?: SkeletonProps;

  required?: boolean; // will also be passed down to via render (might need to make this somehow required in the components that are being rendered)

  // overwritting & extending render method of useController
  render: ({
    field,
    fieldState,
    formState,
  }: {
    field: FormFieldRenderProps<TFieldValues, TName>;
    fieldState: ControllerFieldState;
    formState: UseFormStateReturn<TFieldValues>;
  }) => React.ReactElement;
}

export function FormField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  label,
  LabelProps,
  helperText: helperTextProp,
  HelperTextProps,
  loading = false,
  SkeletonProps,
  required,
  disabled,
  readOnly,
  dirty: dirtyProp,
  error: errorProp,
  submitting: submittingProp,
  render,
  name,
  rules,
  shouldUnregister,
  defaultValue,
  control: controlProp,
}: FormFieldProps<TFieldValues, TName>) {
  const { control } = useFormContext<TFieldValues>();

  const { field, fieldState, formState } = useController({
    name,
    defaultValue,
    rules,
    shouldUnregister,
    control: controlProp ?? control,
  });

  const { isDirty, error } = fieldState;
  const { isSubmitting } = formState;

  const errorMessage = error?.message;
  const hasError = !!errorMessage;

  const helperText = errorMessage || helperTextProp;

  return (
    <>
      {label && (
        <Label {...LabelProps} required={required} className={classNames('mb-1', LabelProps?.className)}>
          {label}
        </Label>
      )}
      {loading && (
        <Skeleton
          variant="rectangular"
          {...SkeletonProps}
          className={SkeletonProps?.className || 'min-h-[38px] rounded-md'}
        />
      )}
      {!loading &&
        render({
          field: {
            ...field,
            dirty: isDirty || dirtyProp,
            error: hasError,
            submitting: isSubmitting || submittingProp,
            disabled,
            readOnly,
            required,
          },
          formState,
          fieldState,
        })}
      {helperText && (
        <HelperText
          {...HelperTextProps}
          className={classNames('mt-1', HelperTextProps?.className)}
          error={hasError}
          disabled={disabled}
        >
          {helperText}
        </HelperText>
      )}
    </>
  );
}

export default FormField;
